class ProductMediaZoom {
  constructor(root) {
    this.root = root;

    this.scale =
      Number.parseFloat(root.dataset.zoomScale) || 2.5;

    this.zoomedSlide = null;
    this.zoomedImage = null;

    this.splide = null;

    this.pointerId = null;

    this.startX = 0;
    this.startY = 0;

    this.startPanX = 0;
    this.startPanY = 0;

    this.panX = 0;
    this.panY = 0;

    this.hasDragged = false;
    this.suppressNextClick = false;

    this.handleClick =
      this.handleClick.bind(this);

    this.handlePointerDown =
      this.handlePointerDown.bind(this);

    this.handlePointerMove =
      this.handlePointerMove.bind(this);

    this.handlePointerUp =
      this.handlePointerUp.bind(this);

    this.handleKeyDown =
      this.handleKeyDown.bind(this);

    this.resetZoom =
      this.resetZoom.bind(this);

    this.init();
  }

  init() {
    this.root.style.setProperty(
      '--product-media-zoom-scale',
      this.scale,
    );

    /*
     * Capture = nos événements passent
     * avant ceux de Splide.
     */
    this.root.addEventListener(
      'pointerdown',
      this.handlePointerDown,
      true,
    );

    this.root.addEventListener(
      'pointermove',
      this.handlePointerMove,
      true,
    );

    this.root.addEventListener(
      'pointerup',
      this.handlePointerUp,
      true,
    );

    this.root.addEventListener(
      'pointercancel',
      this.handlePointerUp,
      true,
    );

    this.root.addEventListener(
      'click',
      this.handleClick,
      true,
    );

    document.addEventListener(
      'keydown',
      this.handleKeyDown,
    );

    this.connectSplide();
  }

  connectSplide(attempt = 0) {
    const sliderComponent =
      this.root.querySelector(
        'slider-component',
      );

    const splide =
      sliderComponent?.slider;

    if (splide?.on) {
      this.splide = splide;

      /*
       * IMPORTANT :
       * on ne reset PLUS sur "drag".
       *
       * Le drag sert justement au déplacement
       * de l'image sur mobile.
       */
      splide.on(
        'move',
        this.resetZoom,
      );

      splide.on(
        'refresh',
        this.resetZoom,
      );

      return;
    }

    if (attempt < 120) {
      requestAnimationFrame(() => {
        this.connectSplide(
          attempt + 1,
        );
      });
    }
  }

  getSlide(target) {
    if (!(target instanceof Element)) {
      return null;
    }

    const slide = target.closest(
      '.product-media-gallery-main-slider ' +
        '.product-media-gallery__media[data-media-type="image"]',
    );

    if (
      !slide ||
      !this.root.contains(slide)
    ) {
      return null;
    }

    return slide;
  }

  getImage(slide) {
    return (
      slide?.querySelector('img') ||
      null
    );
  }

  isFinePointer() {
    return window.matchMedia(
      '(hover: hover) and (pointer: fine)',
    ).matches;
  }

  handleClick(event) {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    /*
     * Navigation Splide.
     */
    if (
      event.target.closest(
        '.splide__arrow, ' +
          '.splide__pagination__page, ' +
          '.product-media-gallery__thumbnail',
      )
    ) {
      this.resetZoom();
      return;
    }

    const slide =
      this.getSlide(event.target);

    if (!slide) {
      return;
    }

    const image =
      this.getImage(slide);

    if (
      !image ||
      !image.contains(event.target)
    ) {
      return;
    }

    /*
     * Après un drag mobile, le navigateur
     * peut générer un click.
     *
     * On ne veut pas que ce click ferme
     * immédiatement le zoom.
     */
    if (this.suppressNextClick) {
      this.suppressNextClick = false;

      event.preventDefault();
      event.stopImmediatePropagation();

      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    /*
     * Deuxième tap / clic :
     * fermeture du zoom.
     */
    if (
      this.zoomedSlide === slide
    ) {
      this.resetZoom();
      return;
    }

    this.activateZoom(
      slide,
      event.clientX,
      event.clientY,
    );
  }

  handlePointerDown(event) {
  /*
   * Desktop :
   * on laisse complètement la souris tranquille.
   *
   * Le desktop utilise le tracking du curseur,
   * pas le drag.
   */
  if (event.pointerType === 'mouse') {
    return;
  }

  /*
   * Mobile / tablette uniquement.
   */
  if (
    !this.zoomedSlide ||
    !this.zoomedImage
  ) {
    return;
  }

  if (
    !(event.target instanceof Node) ||
    !this.zoomedSlide.contains(
      event.target,
    )
  ) {
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();

  this.pointerId =
    event.pointerId;

  this.startX =
    event.clientX;

  this.startY =
    event.clientY;

  this.startPanX =
    this.panX;

  this.startPanY =
    this.panY;

  this.hasDragged = false;

  this.zoomedSlide.classList.add(
    'is-product-media-panning',
  );

  try {
    this.zoomedSlide.setPointerCapture(
      event.pointerId,
    );
  } catch (error) {
    // Pas bloquant.
  }
}

  handlePointerMove(event) {
    if (
      !this.zoomedSlide ||
      !this.zoomedImage
    ) {
      return;
    }

    /*
     * ========================================================
     * DESKTOP
     *
     * Quand aucun bouton n'est enfoncé,
     * le point de zoom suit la souris.
     * ========================================================
     */

    if (
      this.isFinePointer() &&
      this.pointerId === null
    ) {
      const slide =
        this.getSlide(event.target);

      if (
        slide === this.zoomedSlide
      ) {
        this.updateDesktopZoomOrigin(
          event.clientX,
          event.clientY,
        );
      }

      return;
    }

    /*
     * ========================================================
     * MOBILE / TABLETTE
     * ========================================================
     */

    if (
      event.pointerId !==
      this.pointerId
    ) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const deltaX =
      event.clientX -
      this.startX;

    const deltaY =
      event.clientY -
      this.startY;

    if (
      Math.hypot(
        deltaX,
        deltaY,
      ) > 4
    ) {
      this.hasDragged = true;
    }

    const bounds =
      this.getPanBounds();

    this.panX = this.clamp(
      this.startPanX + deltaX,
      -bounds.x,
      bounds.x,
    );

    this.panY = this.clamp(
      this.startPanY + deltaY,
      -bounds.y,
      bounds.y,
    );

    this.applyMobileTransform();
  }

 handlePointerUp(event) {
  /*
   * Desktop :
   * ne surtout pas preventDefault(),
   * sinon le click permettant de dézoomer
   * peut ne jamais être généré.
   */
  if (event.pointerType === 'mouse') {
    return;
  }

  if (
    event.pointerId !==
    this.pointerId
  ) {
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();

  if (this.hasDragged) {
    this.suppressNextClick = true;

    window.setTimeout(() => {
      this.suppressNextClick = false;
    }, 400);
  }

  try {
    if (
      this.zoomedSlide
        ?.hasPointerCapture?.(
          event.pointerId,
        )
    ) {
      this.zoomedSlide
        .releasePointerCapture(
          event.pointerId,
        );
    }
  } catch (error) {
    // Pas bloquant.
  }

  this.zoomedSlide
    ?.classList.remove(
      'is-product-media-panning',
    );

  this.pointerId = null;
  this.hasDragged = false;
}

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.resetZoom();
    }
  }

  activateZoom(
    slide,
    clientX,
    clientY,
  ) {
    this.resetZoom();

    const image =
      this.getImage(slide);

    if (!image) {
      return;
    }

    this.zoomedSlide = slide;
    this.zoomedImage = image;

    this.panX = 0;
    this.panY = 0;

    slide.classList.add(
      'is-product-media-zoomed',
    );

    this.root.classList.add(
      'is-product-media-zoom-active',
    );

    this.loadHighResolutionImage(
      slide,
      image,
    );

    /*
     * DESKTOP
     */
    if (this.isFinePointer()) {
      image.style.setProperty(
        'transform',
        `scale(${this.scale})`,
        'important',
      );

      this.updateDesktopZoomOrigin(
        clientX,
        clientY,
      );
    }

    /*
     * MOBILE
     */
    else {
      image.style.setProperty(
        'transform-origin',
        '50% 50%',
        'important',
      );

      this.applyMobileTransform();
    }
  }

  applyMobileTransform() {
    if (!this.zoomedImage) {
      return;
    }

    this.zoomedImage.style.setProperty(
      'transform',
      `translate3d(
        ${this.panX}px,
        ${this.panY}px,
        0
      ) scale(${this.scale})`,
      'important',
    );
  }

  updateDesktopZoomOrigin(
    clientX,
    clientY,
  ) {
    if (
      !this.zoomedSlide ||
      !this.zoomedImage
    ) {
      return;
    }

    const rect =
      this.zoomedSlide
        .getBoundingClientRect();

    if (
      !rect.width ||
      !rect.height
    ) {
      return;
    }

    const x =
      this.clamp(
        (
          (clientX - rect.left) /
          rect.width
        ) * 100,
        0,
        100,
      );

    const y =
      this.clamp(
        (
          (clientY - rect.top) /
          rect.height
        ) * 100,
        0,
        100,
      );

    this.zoomedImage.style.setProperty(
      '--product-media-zoom-x',
      `${x}%`,
    );

    this.zoomedImage.style.setProperty(
      '--product-media-zoom-y',
      `${y}%`,
    );

    this.zoomedImage.style.setProperty(
      'transform-origin',
      `${x}% ${y}%`,
      'important',
    );
  }

  getPanBounds() {
    if (!this.zoomedSlide) {
      return {
        x: 0,
        y: 0,
      };
    }

    const rect =
      this.zoomedSlide
        .getBoundingClientRect();

    /*
     * Déplacement maximal disponible
     * avec une image zoomée depuis le centre.
     */
    return {
      x: Math.max(
        0,
        (
          rect.width *
          (this.scale - 1)
        ) / 2,
      ),

      y: Math.max(
        0,
        (
          rect.height *
          (this.scale - 1)
        ) / 2,
      ),
    };
  }

  clamp(
    value,
    min,
    max,
  ) {
    return Math.min(
      max,
      Math.max(
        min,
        value,
      ),
    );
  }

  resetZoom() {
    if (!this.zoomedSlide) {
      return;
    }

    const slide =
      this.zoomedSlide;

    const image =
      this.zoomedImage;

    slide.classList.remove(
      'is-product-media-zoomed',
    );

    slide.classList.remove(
      'is-product-media-panning',
    );

    this.root.classList.remove(
      'is-product-media-zoom-active',
    );

    if (image) {
      image.style.setProperty(
        'transform',
        'translate3d(0, 0, 0) scale(1)',
        'important',
      );

      image.style.setProperty(
        'transform-origin',
        '50% 50%',
        'important',
      );

      image.style.removeProperty(
        '--product-media-zoom-x',
      );

      image.style.removeProperty(
        '--product-media-zoom-y',
      );

      window.setTimeout(() => {
        if (
          !slide.classList.contains(
            'is-product-media-zoomed',
          )
        ) {
          image.style.removeProperty(
            'transform',
          );

          image.style.removeProperty(
            'transform-origin',
          );
        }
      }, 320);
    }

    this.zoomedSlide = null;
    this.zoomedImage = null;

    this.pointerId = null;

    this.panX = 0;
    this.panY = 0;

    this.startPanX = 0;
    this.startPanY = 0;

    this.hasDragged = false;
    this.suppressNextClick = false;
  }

  loadHighResolutionImage(
    slide,
    image,
  ) {
    const zoomSrc =
      slide.dataset.zoomSrc;

    if (
      !zoomSrc ||
      image.dataset
        .zoomHighResLoaded ===
        'true'
    ) {
      return;
    }

    image.removeAttribute(
      'srcset',
    );

    image.removeAttribute(
      'sizes',
    );

    image.src = zoomSrc;

    image.dataset
      .zoomHighResLoaded =
      'true';
  }
}


/*
 * ============================================================
 * INITIALISATION
 * ============================================================
 */

const zoomInstances =
  new WeakMap();

function initProductMediaZoom(
  root = document,
) {
  const galleries = [];

  if (
    root instanceof Element &&
    root.matches(
      'product-media-gallery[data-zoom-enabled="true"]',
    )
  ) {
    galleries.push(root);
  }

  root
    .querySelectorAll?.(
      'product-media-gallery[data-zoom-enabled="true"]',
    )
    .forEach((gallery) => {
      galleries.push(gallery);
    });

  galleries.forEach(
    (gallery) => {
      if (
        zoomInstances.has(gallery)
      ) {
        return;
      }

      zoomInstances.set(
        gallery,
        new ProductMediaZoom(
          gallery,
        ),
      );
    },
  );
}

initProductMediaZoom();


document.addEventListener(
  'shopify:section:load',
  (event) => {
    initProductMediaZoom(
      event.target,
    );
  },
);

document.addEventListener(
  'shopify:block:load',
  (event) => {
    initProductMediaZoom(
      event.target,
    );
  },
);